import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { CodeExecutionResultSummary } from 'stores/CodeExecuteStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';

type Props = {
  /** アクティブファイルが実行可能拡張子か */
  runnable: boolean;
  results: CodeExecutionResultSummary[];
  loading: boolean;
  /** リバート可否（他人のファイルで編集不可なら false） */
  editable: boolean;
  onClickResult: (seq: number) => void;
  onRevert: (seq: number) => void;
  onDelete: (seq: number) => void;
};

/** 成功（Accepted）判定 */
const isSuccess = (statusId: number): boolean => statusId === 3;

const statusColor = (statusId: number): 'success' | 'error' | 'warning' | 'default' => {
  if (statusId === 3) return 'success';
  if (statusId === 5 || statusId === 13) return 'warning';
  if (statusId >= 4) return 'error';
  return 'default';
};

/**
 * 実行履歴パネル（左ペイン「実行履歴」）。
 * 現在アクティブなファイルが実行可能拡張子の場合に履歴一覧を表示する。
 */
const RunHistoryPanel: React.FC<Props> = ({
  runnable,
  results,
  loading,
  editable,
  onClickResult,
  onRevert,
  onDelete,
}) => {
  // 成功（Accepted）のみ表示するフィルタ
  const [successOnly, setSuccessOnly] = React.useState(false);
  const displayResults = successOnly ? results.filter((r) => isSuccess(r.statusId)) : results;

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.25, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color: 'text.secondary', letterSpacing: '0.05em' }}
        >
          実行履歴
        </Typography>
        <Tooltip title={successOnly ? '全て表示' : '成功のみ表示'}>
          <IconButton
            size="small"
            onClick={() => setSuccessOnly((v) => !v)}
            color={successOnly ? 'success' : 'default'}
          >
            {successOnly ? (
              <CheckCircleIcon sx={{ fontSize: 18 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1 }}>
        {!runnable ? (
          <Typography variant="caption" color="text.secondary">
            表示できる実行履歴がありません
          </Typography>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : displayResults.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {successOnly ? '成功した実行履歴がありません' : '実行履歴がありません'}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {displayResults.map((r) => (
              <Box
                key={r.seq}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1,
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={0.5}
                >
                  <Box
                    onClick={() => onClickResult(r.seq)}
                    sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" fontWeight={600}>
                        #{r.seq}
                      </Typography>
                      <Chip
                        label={r.statusDescription}
                        size="small"
                        color={statusColor(r.statusId)}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {r.time != null ? `${r.time}s` : '-'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatDateTime(r.createdAt)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0}>
                    <Tooltip title="この実行時点に戻す">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onRevert(r.seq)}
                          disabled={!editable}
                        >
                          <ReplayIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="履歴を削除">
                      <IconButton size="small" onClick={() => onDelete(r.seq)}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default RunHistoryPanel;
